import FilterDateRangePicker from "./FilterDateRangePicker";
import CheckboxActiveIcon from "app/components/Icons/CheckboxActiveIcon";
import CheckboxIcon from "app/components/Icons/CheckboxIcon";
import DeleteIcon from "app/components/Icons/DeleteIcon";
import { DropdownIcon } from "app/components/Icons";
import MoreHorizontalIcon from "app/components/Icons/MoreHorizontalIcon";
import SearchIcon from "app/components/Icons/SearchIcon";
import IconText from "app/components/IconText";
import clsx from "clsx";
import React, { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { DateRangeFilterValue, Filter, FilterOptionItem, FilterPickerType, FilterSelectedValue } from "../../types";
import { getDateRangeLabel, getSelectedOptionLabels, isDateRangeApplied, isDateRangeValue } from "./filterDropdownHelpers";


interface TicketsFilterDropdownProps {
  option: Filter;
  selectedOptions: FilterSelectedValue;
  onSelectOption: (option: FilterSelectedValue) => void;
  onDeleteFilter: (filter: { id: string; options: FilterOptionItem[] }) => void;
}

const filterOptionsBySearch = (
  items: FilterOptionItem[],
  searchTerm: string,
) => {
  const query = searchTerm.toLowerCase();
  return items.filter((item) => item.label.toLowerCase().includes(query));
};

export default function TicketsFilterDropdown({
  option,
  selectedOptions,
  onSelectOption,
  onDeleteFilter,
}: TicketsFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectAll, setSelectAll] = useState(false);

  const isDateFilter = option.pickerType === FilterPickerType.DATE_RANGE;
  const isStatusFilter = option.pickerType === FilterPickerType.STATUS;
  const isTagsFilter = option.pickerType === FilterPickerType.TAGS;
  const selectedIds = Array.isArray(selectedOptions) ? selectedOptions : [];
  const dateRangeValue: DateRangeFilterValue = isDateRangeValue(selectedOptions)
    ? selectedOptions
    : { startDate: null, endDate: null };

  const filteredOptionItems = filterOptionsBySearch(option.options, searchTerm);

  const isSelected = isDateFilter
    ? isDateRangeApplied(dateRangeValue)
    : selectedIds.length > 0;

  const clonedIcon = React.cloneElement(option.icon as React.ReactElement, {
    color: isSelected ? "#5A8DF1" : "#79808A",
  });

  const selectedText = isDateFilter
    ? ""
    : getSelectedOptionLabels(option.options, selectedIds);

  const triggerLabel = isDateFilter
    ? isDateRangeApplied(dateRangeValue)
      ? getDateRangeLabel(option.name, dateRangeValue)
      : option.name
    : isSelected
      ? `${option.name}: ${selectedText}`
      : option.name;

  const handleSelectAll = () => {
    if (selectAll) {
      onSelectOption([]);
    } else {
      onSelectOption(option.options.map((item) => item.id));
    }
    setSelectAll(!selectAll);
  };

  const handleDeleteFilter = () => {
    onDeleteFilter(option);
    setOpen(false);
  };

  const handleDateRangeSelect = (dateRange: DateRangeFilterValue) => {
    onSelectOption(dateRange);
  };

  const toggleOptionId = (optionId: string) => {
    if (selectedIds.includes(optionId)) {
      onSelectOption(selectedIds.filter((id) => id !== optionId));
      return;
    }
    onSelectOption([...selectedIds, optionId]);
  };

  const renderCheckboxOptions = () => (
    <div className="max-h-[220px] overflow-y-auto custom-scrollbar">
      {filteredOptionItems.map((item) => (
        <DropdownMenu.Item
          key={item.id}
          className="px-4 py-3 hover:bg-[#F4F5F7] focus:outline-none cursor-pointer flex flex-row items-center gap-[10px]"
          onClick={(e) => {
            e.preventDefault();
            toggleOptionId(item.id);
          }}
        >
          {selectedIds.includes(item.id) ? (
            <CheckboxActiveIcon />
          ) : (
            <CheckboxIcon />
          )}
          <span className="text-sm leading-[21px] text-secondary font-medium mt-[2px]">
            {item.label}
          </span>
        </DropdownMenu.Item>
      ))}
    </div>
  );

  const renderStatusOptions = () => (
    <div className="max-h-[220px] overflow-y-auto custom-scrollbar">
      {filteredOptionItems.map((status) => (
        <DropdownMenu.Item
          key={status.id}
          className="px-4 py-3 hover:bg-[#F4F5F7] focus:outline-none cursor-pointer flex flex-row items-center gap-[10px]"
          onClick={(e) => {
            e.preventDefault();
            toggleOptionId(status.id);
          }}
        >
          {selectedIds.includes(status.id) ? (
            <CheckboxActiveIcon />
          ) : (
            <CheckboxIcon />
          )}
          <span
            className="text-sm leading-[21px] font-medium mt-[2px] truncate"
            style={{ color: status.color || "#79808A" }}
          >
            {status.label}
          </span>
        </DropdownMenu.Item>
      ))}
    </div>
  );

  const renderTagOptions = () => (
    <div className="max-h-[220px] overflow-y-auto custom-scrollbar">
      {filteredOptionItems.map((tag) => (
        <DropdownMenu.Item
          key={tag.id}
          className="px-4 py-3 hover:bg-[#F4F5F7] focus:outline-none cursor-pointer flex flex-row items-center gap-[10px]"
          onClick={(e) => {
            e.preventDefault();
            toggleOptionId(tag.id);
          }}
        >
          {selectedIds.includes(tag.id) ? (
            <CheckboxActiveIcon />
          ) : (
            <CheckboxIcon />
          )}
          <span
            className="text-[10px] leading-tight font-medium truncate px-1.5 py-[5.5px] rounded"
            style={{
              backgroundColor: tag.color ? `${tag.color}18` : "#E9ECF2",
              color: tag.color || "#79808A",
            }}
          >
            {tag.label}
          </span>
        </DropdownMenu.Item>
      ))}
    </div>
  );

  const renderDateRangePicker = () => (
    <FilterDateRangePicker
      value={dateRangeValue}
      onChange={handleDateRangeSelect}
    />
  );

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          className={clsx(
            "text-sm font-normal text-secondary h-[32px] max-w-[270px] flex flex-row items-center gap-[6px] justify-center bg-white border rounded-[54px] px-[10px] text-ellipsis whitespace-nowrap overflow-hidden",
            open
              ? "border-secondary"
              : isSelected
                ? "border-primary"
                : "border-[#E9ECF2]",
          )}
        >
          <IconText
            icon={clonedIcon}
            text={triggerLabel}
            className={clsx(
              "text-sm font-medium mt-[2px] max-w-[250px] text-ellipsis whitespace-nowrap overflow-hidden",
              isSelected ? "text-primary" : "text-secondary",
            )}
          />
          <DropdownIcon
            size={20}
            color={isSelected ? "#5A8DF1" : "#79808A"}
          />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={clsx(
            "bg-white py-[10px] px-0",
            isDateFilter ? "w-[520px] max-w-[520px]" : "w-[250px]",
          )}
          align="start"
        >
          <div className="flex items-center justify-between pl-4 pr-1 py-[12px] text-secondary tracking-[0.56px] text-sm font-semibold leading-[21px] uppercase border-b border-[#E9ECF2]">
            <span>{option.name}</span>
            <div className="flex items-center gap-2">
              {!isDateFilter && option.options.length > 0 && (
                <button
                  className="text-blue-600 text-xs font-medium focus:outline-none text-[#5A8DF1] font-weight-500"
                  onClick={handleSelectAll}
                >
                  {selectAll ? "Unselect All" : "Select All"}
                </button>
              )}

              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button className="flex items-center justify-center w-6 h-6 rounded hover:bg-[#E9ECF2]">
                    <MoreHorizontalIcon size={16} color="#79808A" />
                  </button>
                </DropdownMenu.Trigger>

                <DropdownMenu.Content
                  className="min-w-[160px] bg-white rounded-[4px] border border-[#E9ECF2] shadow-md py-[5px] z-50"
                  sideOffset={5}
                  align="end"
                >
                  <DropdownMenu.Item
                    className="flex items-center gap-2 px-4 py-2 text-sm cursor-pointer hover:bg-[#E9ECF2] rounded-[4px] normal-case font-normal"
                    onClick={handleDeleteFilter}
                  >
                    <DeleteIcon size={20} color="#79808A" />
                    Delete filter
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </div>
          </div>

          {option.isSearchable && !isDateFilter && (
            <div className="relative px-4 py-2 border-b border-[#E9ECF2]">
              <span className="absolute left-6 top-[50%] transform -translate-y-1/2">
                <SearchIcon color="#79808A" size={"16"} />
              </span>
              <input
                type="text"
                placeholder={`Search by ${option.name.toLocaleLowerCase()} name...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full text-sm border rounded-[4px] outline-none focus:border-blue-400"
              />
            </div>
          )}

          {isDateFilter
            ? renderDateRangePicker()
            : isStatusFilter
              ? renderStatusOptions()
              : isTagsFilter
                ? renderTagOptions()
                : renderCheckboxOptions()}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
